import { useEffect, useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiProviderConfig, AiTask, AiTaskAssignment } from '@mailpilot/shared';
import { BarChart3, FileSearch, ListChecks, MessageCircleQuestion, PenLine, ScanSearch } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { Button, Card, CardBody, CardDivider, CardHeader, EmptyState, ErrorState, Input, Select, SkeletonLines, useToast } from '../ui';
import { FormFooter } from './SettingsPrimitives';
import type { KindInfo } from './AiProviderEditor';

export const TASK_LABELS: Record<AiTask, string> = {
  draft: 'Reply drafting',
  analysis: 'Inbox analysis',
  attachment: 'Attachment extraction',
  question: 'Inbox questions',
};
const TASK_HINTS: Record<AiTask, string> = {
  draft: 'Writes suggested replies and automatic responses.',
  analysis: 'Categorises, summarises and spots what needs attention.',
  attachment: 'Reads documents to answer from them.',
  question: 'Answers questions you ask about your inbox.',
};
const TASK_ICONS: Record<AiTask, typeof PenLine> = { draft: PenLine, analysis: ScanSearch, attachment: FileSearch, question: MessageCircleQuestion };
const TASKS: AiTask[] = ['draft', 'analysis', 'attachment', 'question'];

function normalise(tasks: AiTaskAssignment[]): AiTaskAssignment[] {
  return TASKS.map((task) => tasks.find((t) => t.task === task) ?? { task, providerId: null, model: null });
}

export function AiTasksCard({ tasks, providers, kinds }: { tasks: AiTaskAssignment[]; providers: AiProviderConfig[]; kinds: KindInfo[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const baseId = useId();
  const [rows, setRows] = useState(() => normalise(tasks));
  useEffect(() => setRows(normalise(tasks)), [tasks]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(normalise(tasks));

  const save = useMutation({
    mutationFn: () => api('PUT /api/ai/tasks', { body: { tasks: rows.map((r) => ({ ...r, model: r.model?.trim() || null })) } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.aiProviders });
      toast.success('Model assignments saved');
    },
    onError: (e) => toast.error(e),
  });

  const setRow = (task: AiTask, patch: Partial<AiTaskAssignment>) => setRows((rs) => rs.map((r) => (r.task === task ? { ...r, ...patch } : r)));
  const suggestions = (providerId: string | null) => {
    const p = providers.find((x) => x.id === providerId);
    const k = p ? kinds.find((x) => x.kind === p.kind) : undefined;
    return k?.suggestedModels ?? [];
  };

  return (
    <Card>
      <CardHeader icon={<ListChecks className="h-5 w-5" aria-hidden />} title="Models per task" description="Use a different provider or model for each job. Leave the model blank to use the provider's default." />
      {providers.length === 0 ? (
        <EmptyState className="!py-8" icon={<ListChecks className="h-6 w-6" aria-hidden />} title="Nothing to assign yet" description="Add a provider first, then choose which model handles each task here." />
      ) : (
        <>
          <CardDivider className="mt-5" />
          <CardBody className="!py-2">
            <div className="divide-y divide-line/70">
              {rows.map((r) => {
                const listId = `${baseId}-${r.task}`;
                const p = providers.find((x) => x.id === r.providerId);
                const Icon = TASK_ICONS[r.task];
                return (
                  <div key={r.task} className="grid items-center gap-x-4 gap-y-2 py-4 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)]">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className={clsx('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]', r.providerId ? 'bg-primary-soft text-primary-ink' : 'bg-sunken text-muted')}>
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-ink">{TASK_LABELS[r.task]}</p>
                        <p className="text-xs text-muted">{TASK_HINTS[r.task]}</p>
                      </div>
                    </div>
                    <Select aria-label={`Provider for ${TASK_LABELS[r.task]}`} value={r.providerId ?? ''} onChange={(e) => setRow(r.task, { providerId: e.target.value || null, model: null })}>
                      <option value="">Not assigned</option>
                      {providers.map((pr) => (
                        <option key={pr.id} value={pr.id}>
                          {pr.label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      aria-label={`Model for ${TASK_LABELS[r.task]}`}
                      list={listId}
                      disabled={!r.providerId}
                      value={r.model ?? ''}
                      placeholder={p ? `Default: ${p.defaultModel}` : 'Choose a provider first'}
                      onChange={(e) => setRow(r.task, { model: e.target.value })}
                      className="font-mono text-xs"
                    />
                    <datalist id={listId}>
                      {suggestions(r.providerId).map((m) => (
                        <option key={m} value={m} />
                      ))}
                    </datalist>
                  </div>
                );
              })}
            </div>
          </CardBody>
          <CardBody className="!pt-0">
            <FormFooter note={dirty ? 'Unsaved changes' : undefined}>
              {dirty && (
                <Button size="sm" onClick={() => setRows(normalise(tasks))} disabled={save.isPending}>
                  Revert
                </Button>
              )}
              <Button variant="primary" size="sm" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
                Save assignments
              </Button>
            </FormFooter>
          </CardBody>
        </>
      )}
    </Card>
  );
}

const usd = (n: number) => `$${n < 1 && n > 0 ? n.toFixed(4) : n.toFixed(2)}`;

export function AiUsageCard({ providers, builtinLabel }: { providers: AiProviderConfig[]; builtinLabel?: string }) {
  const q = useQuery({ queryKey: qk.aiUsage, queryFn: () => api('GET /api/ai/usage') });
  const label = (r: { providerId: string | null; builtin: boolean }) => (r.builtin ? (builtinLabel ?? 'MailPilot AI') : providers.find((p) => p.id === r.providerId)?.label ?? 'Deleted provider');
  const rows = q.data?.rows ?? [];
  const total = rows.reduce((s, r) => s + r.costUsd, 0);
  const requests = rows.reduce((s, r) => s + r.requests, 0);

  return (
    <Card>
      <CardHeader
        icon={<BarChart3 className="h-5 w-5" aria-hidden />}
        title="Usage this month"
        description="Requests, tokens and estimated cost reported by each provider."
        actions={
          rows.length > 0 ? (
            <div className="text-right">
              <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">Total</p>
              <p className="text-base font-semibold tabular-nums text-ink">{usd(total)}</p>
            </div>
          ) : undefined
        }
      />
      {q.isLoading ? (
        <CardBody>
          <SkeletonLines lines={4} />
        </CardBody>
      ) : q.isError ? (
        <CardBody>
          <ErrorState compact error={q.error} onRetry={() => void q.refetch()} title="Couldn't load usage" />
        </CardBody>
      ) : rows.length === 0 ? (
        <EmptyState className="!py-8" icon={<BarChart3 className="h-6 w-6" aria-hidden />} title="No AI requests yet" description="Usage appears here as soon as a draft, analysis or question goes through a provider." />
      ) : (
        <>
          <CardDivider className="mt-5" />
          <div className="overflow-x-auto px-6 pb-2 scroll-thin">
            <table className="w-full min-w-[560px] text-left text-[0.8125rem]">
              <thead>
                <tr className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
                  <th scope="col" className="py-3 pr-3 font-semibold">Provider</th>
                  <th scope="col" className="py-3 pr-3 font-semibold">Task</th>
                  <th scope="col" className="py-3 pr-3 font-semibold">Model</th>
                  <th scope="col" className="py-3 pr-3 text-right font-semibold">Requests</th>
                  <th scope="col" className="py-3 pr-3 text-right font-semibold">Tokens in / out</th>
                  <th scope="col" className="py-3 text-right font-semibold">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/70 border-t border-line/70">
                {rows.map((r, i) => (
                  <tr key={`${r.providerId}-${r.task}-${r.model}-${i}`} className="text-ink">
                    <td className="py-2.5 pr-3 font-medium">{label(r)}</td>
                    <td className="py-2.5 pr-3">{TASK_LABELS[r.task]}</td>
                    <td className="py-2.5 pr-3 font-mono text-xs text-muted">{r.model}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{r.requests.toLocaleString()}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-muted">
                      {r.inputTokens.toLocaleString()} / {r.outputTokens.toLocaleString()}
                    </td>
                    <td className="py-2.5 text-right tabular-nums">{usd(r.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-line/70 font-semibold text-ink">
                  <td colSpan={3} className="py-3 pr-3">
                    Total
                  </td>
                  <td className="py-3 pr-3 text-right tabular-nums">{requests.toLocaleString()}</td>
                  <td />
                  <td className="py-3 text-right tabular-nums">{usd(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}
